import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Linkedin, Twitter, Facebook, Instagram, Youtube } from "lucide-react";
import { CONTACT as STATIC_CONTACT } from "@/data/site";
import { useSettings, useMenu } from "@/lib/content";
import NewsletterSignup from "@/components/NewsletterSignup";

const SOCIAL_ICONS = { linkedin: Linkedin, twitter: Twitter, facebook: Facebook, instagram: Instagram, youtube: Youtube };

export default function Footer() {
  const CONTACT = useSettings(STATIC_CONTACT);
  const { menu: MENU } = useMenu();
  const social = CONTACT.social || {};
  const socialLinks = Object.entries(SOCIAL_ICONS).filter(([k]) => social[k]);
  return (
    <footer data-testid="main-footer" className="bg-white border-t border-slate-200">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
          <div className="col-span-2">
            <Link to="/" className="flex items-center">
              <img src="/logo-sm.png" alt="CLI Secure" className="h-14 w-auto" />
            </Link>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed max-w-xs">
              London's managed IT, cyber security and connectivity partner for growing businesses.
            </p>
            <div className="mt-6 space-y-2.5 text-sm text-slate-600">
              <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-2.5 hover:text-purple-900 transition-colors">
                <Phone className="h-4 w-4 text-purple-900" /> {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2.5 hover:text-purple-900 transition-colors">
                <Mail className="h-4 w-4 text-purple-900" /> {CONTACT.email}
              </a>
              <a href={`mailto:${CONTACT.supportEmail}`} className="flex items-center gap-2.5 hover:text-purple-900 transition-colors">
                <Mail className="h-4 w-4 text-purple-900" /> {CONTACT.supportEmail}
              </a>
              <p className="flex items-center gap-2.5">
                <MapPin className="h-4 w-4 text-purple-900" /> {CONTACT.address}
              </p>
            </div>
            {socialLinks.length > 0 && (
              <div className="mt-6 flex items-center gap-3">
                {socialLinks.map(([k, Icon]) => (
                  <a key={k} href={social[k]} target="_blank" rel="noopener noreferrer" aria-label={k}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:border-purple-300 hover:text-purple-900 transition-colors">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {MENU.slice(0, 4).map((entry) => (
            <div key={entry.key}>
              <p className="font-cli text-[11px] lowercase tracking-wider text-slate-400 mb-4">{entry.label}</p>
              <ul className="space-y-2.5">
                {(entry.columns ? entry.columns.flatMap((c) => c.items) : entry.items).slice(0, 5).map((it) => (
                  <li key={it.title}>
                    <Link to={it.href || "#"} className="text-[13px] text-slate-600 hover:text-purple-900 transition-colors">
                      {it.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Optional newsletter signup — renders nothing unless enabled in Site settings. */}
        <NewsletterSignup />

        <div className="mt-14 pt-6 border-t border-slate-200 flex flex-col sm:flex-row justify-between gap-3 text-[13px] text-slate-500">
          <p>© {new Date().getFullYear()} CLI Secure Ltd. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-purple-900 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-purple-900 transition-colors">Terms</Link>
            <Link to="/cookies" className="hover:text-purple-900 transition-colors">Cookies</Link>
            <Link to="/legal" className="hover:text-purple-900 transition-colors">Service Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
